import { Minus } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material/styles";

import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { ExecutionPanelMenu } from "@src/features/ai/components/WorkflowEditor/execution/panel/header/ExecutionPanelMenu";
import { ExecutionPanelTitle } from "@src/features/ai/components/WorkflowEditor/execution/panel/header/ExecutionPanelTitle";

/** Figma "Atlas / Rail header": every control is a 40px box, 8px above and below its 16px glyph. */
const CONTROL_SX: SxProps<Theme> = {
  borderRadius: "6px",
  color: (theme) => theme.igIconColors.light,
  height: 40,
  py: 2,
  width: 40,
};

/**
 * Figma "Atlas / Rail header" (516:3173): the brand row, then the hairline
 * that separates it from the conversation, 12px below.
 *
 * The right-hand controls are the ⋮ menu (New Chat / Copy Chat ID / Copy
 * Transcript) and, after it, the minus that hides the rail. The minus is the
 * panel's close action, so it renders only where the host hands the panel one
 * — a surface that must keep Atlas on screen (editing or creating a rule)
 * simply passes none, and the ⋮ stays.
 *
 * On an existing rule the minus hides the rail instead (ENG-7644): the host
 * passes `onHideRail` exactly where hiding is allowed, and a toolbar button
 * brings the rail back. It wins over `onClose` — the below-`lg` dialog never
 * shows a rule's hideable rail.
 *
 * Full screen (Figma "Atlas full screen", 204:3989) swaps the minus for an
 * `Exit chat` button: there is no rail to hide, only a conversation to leave.
 * The host passes `onExitChat` exactly then, and owns what leaving means.
 *
 * The 24px of bottom padding, with the conversation's own 4px offset and the
 * 4px inset its scroller keeps for focus rings, is the 32px Figma puts between
 * this divider and the first message.
 */
export const AtlasRailHeader = ({
  onExitChat,
  onHideRail,
}: {
  /** Full screen only: leave the conversation. Null on the docked rail. */
  onExitChat: (() => void) | null;
  /** Hide the docked rail. Null where it must stay on screen. */
  onHideRail: (() => void) | null;
}) => {
  const { onClose, closeLabel } = useExecutionPanelContext();
  const reducedMotion = useReducedMotion();
  const onMinus = onHideRail ?? onClose;
  const minusLabel = onHideRail ? "Hide Atlas" : (closeLabel ?? "Close Atlas");

  return (
    <Stack sx={{ flexShrink: 0, gap: 3, pb: 6, width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: 40 }}
      >
        <ExecutionPanelTitle />
        <Stack
          direction="row"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <motion.div
            aria-hidden={!onExitChat}
            inert={!onExitChat}
            initial={false}
            animate={{ opacity: onExitChat ? 1 : 0 }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : { duration: 0.16, ease: [0.4, 0, 1, 1] }
            }
            style={{
              alignItems: "center",
              display: "flex",
              pointerEvents: onExitChat ? "auto" : "none",
            }}
          >
            <ExecutionPanelMenu iconSize={16} buttonSx={CONTROL_SX} />
            <IgButton
              variant="outlined"
              color="secondary"
              onClick={() => onExitChat?.()}
              sx={{ height: 36, ml: 2, px: 4, whiteSpace: "nowrap" }}
            >
              Exit chat
            </IgButton>
          </motion.div>
          <motion.div
            aria-hidden={Boolean(onExitChat)}
            inert={Boolean(onExitChat)}
            initial={false}
            animate={{ opacity: onExitChat ? 0 : 1 }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : onExitChat
                  ? { duration: 0.16, ease: [0.4, 0, 1, 1] }
                  : { duration: 0.2, delay: 0.36, ease: [0, 0, 0.2, 1] }
            }
            style={{
              alignItems: "center",
              display: "flex",
              pointerEvents: onExitChat ? "none" : "auto",
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            <ExecutionPanelMenu iconSize={16} buttonSx={CONTROL_SX} />
            {onMinus ? (
              <IgIconButton
                aria-label={minusLabel}
                onClick={onMinus}
                sx={CONTROL_SX}
              >
                <Minus size={16} />
              </IgIconButton>
            ) : null}
          </motion.div>
        </Stack>
      </Stack>
      <Divider sx={{ borderColor: (theme) => theme.igBorders.core.disabled }} />
    </Stack>
  );
};
